import { MATCH_STATE } from '../machine';
import { ok, reject } from '../result';
import type { DomainResult } from '../result';
import { freshHypothesisProblem } from './setup';
import type {
  Decision,
  FlagResolutionOp,
  ResolveFlagCommand,
  ResolveFlagSnapshot,
} from './types';
import type { FlagResolutionRow, HypothesisRow } from '../../types';

/**
 * FLAG-3 / DIST-1 — **resolving a flag** (build unit U-13c).
 *
 * > FLAG-3: *"Flags can be reviewed at Halftime and at Triage: play back, promote to a hypothesis,
 * > or dismiss."*
 * >
 * > DIST-1: *"Resolution is recorded as an append-only `FlagResolution` row — never as a mutation of
 * > the flag `Observation`, which INV-1 forbids — so a resolved flag MUST NOT reappear in the
 * > queue."*
 *
 * ── Why this is its own module ──────────────────────────────────────────────
 *
 * `commands/halftime.ts` earns its narrow op types by containing only handlers that cannot touch a
 * `Hypothesis`; this one creates them, so it would take that property away exactly as
 * `commands/halftimeManage.ts` would have. The seam is the same one U-13a and U-13b used — *what the
 * activity writes* — and it puts FLAG-3 beside PRE-3's add rather than beside HT-5's promote, which
 * is where a reader looking for "what creates a hypothesis at halftime" would go.
 *
 * ── The window is now open in play too (U-14a-ii), because the surface exists ─
 *
 * U-13c left this window at `halftime` alone and said why; U-14a-i widened it to the three
 * post-match states and stopped at the in-play boundary for the same reason, **and was right to**:
 * a command window with no surface able to drive it is an unguarded write path whose negative no
 * pass can prove. §16.4 R-6 ratifies that stop and the design then paid what it owed — §2.3a sites
 * the in-play entry as a row in the **existing `⋯` overflow sheet** (*Review flags (n)*), beside
 * SES-6's Abandon, on every in-match capture surface.
 *
 * So the window is now every state in which a flag can be looked at: HT-4's break, the two halves
 * §2.3a reaches, and the three post-match states the screen occupies from the Full Time offer
 * (SES-11). `firstHalf` and `secondHalf` are admitted **because and only because** that row exists.
 *
 * | State | Why it stays shut |
 * |---|---|
 * | `idle`, `preMatch` | a flag is an in-play capture; before kickoff there is nothing to resolve |
 * | `archived` | DIST-5 archived the match. `PassTriage` is already past, INV-6's set is fixed, and a resolution here would silently change the unresolved count of a completed match |
 *
 * §2.3 is what makes the in-play arm safe to open rather than merely reachable: visiting the screen
 * changes **no** `Match.state`, so a promote issued at minute 60 arrives carrying `secondHalf` and
 * the match is still in its half when the user taps Back. Nothing in this handler writes an anchor
 * or dispatches a transition, and `PassTriage` — DIST-1's lifecycle step — is a different command
 * with no row from an in-play state, so the dead-spell visit cannot close the step by accident.
 *
 * Three windows now exist in this codebase and no two are the same, which is the point:
 *
 * | Window | States | Why it differs |
 * |---|---|---|
 * | this one | `halftime`, `firstHalf`, `secondHalf`, `fullTime`, `triage`, `distill` | wherever a flag can be reviewed |
 * | `handleSwapBoardTile` | the in-match states | LOCK-6's composition change needs a **live board**, which the post-match states no longer have |
 * | `handleAdjustBoundaries` | `fullTime`, `triage`, `distill` | §10.1 keeps the product's most destructive control off every surface reachable in a half — and off `halftime` too, since a boundary moved during the break moves a half still being played |
 *
 * ── The queue is derived, so "resolved" cannot become a second piece of state ─
 *
 * `repos/flagResolutions.listUnresolvedFlags` subtracts resolutions from flags. Nothing here writes
 * a `resolved` field anywhere, because there is no such field — which is what makes DIST-1's
 * *"MUST NOT reappear"* a property of the schema rather than of this handler remembering to set one.
 */

const MODULE = 'commands/flags';

/**
 * FLAG-3 / DIST-1's window, as a set rather than a chain of `!==`.
 *
 * A `Set` over `MATCH_STATE` members rather than state literals, because `ses-1/match-state-literals`
 * forbids the literals outside `domain/` and a named map is the checked form: renaming a state breaks
 * this line instead of leaving a stale string. The three states left out are the whole of the
 * negative (`idle`, `preMatch`, `archived` — see the module note) and `flags.test.ts` asserts each of
 * them by name, with the reason, rather than as "everything else".
 */
const RESOLVABLE_IN: ReadonlySet<string> = new Set<string>([
  MATCH_STATE.firstHalf,
  MATCH_STATE.halftime,
  MATCH_STATE.secondHalf,
  MATCH_STATE.fullTime,
  MATCH_STATE.triage,
  MATCH_STATE.distill,
]);

/**
 * FLAG-3 — promote or dismiss **one** flag.
 *
 * ── The order of the guards is the order of the questions ───────────────────
 *
 * State, then *is this a flag of this match*, then *has it already been dealt with*, then *does the
 * plan make sense*. Each is a different sentence to the user and each is read from the snapshot the
 * transaction opened with, never from what the surface last rendered.
 *
 * ── `alreadyResolved` is checked even though the index would abort anyway ────
 *
 * `&observationId` is unique, so a second resolution cannot commit whatever this handler does. But
 * an index abort surfaces as a `StorageError` — *"could not record the flag resolution"* — and a
 * double-tap on a sheet the user is reading is an ordinary event, not a fault. Two mechanisms, and
 * neither is redundant: the index makes the duplicate **impossible**, this check makes it
 * **explicable**.
 */
export function handleResolveFlag(
  cmd: ResolveFlagCommand,
  snap: ResolveFlagSnapshot,
): Decision<FlagResolutionOp> {
  const match = snap.match;
  if (match === undefined) {
    return reject(MODULE, cmd.matchId, 'ERR_MATCH_NOT_FOUND', 'That match no longer exists.');
  }

  if (!RESOLVABLE_IN.has(match.state)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_FLAGS_NOT_REVIEWABLE',
      match.state === MATCH_STATE.archived
        ? 'This match is archived. Its flags were settled before it was distilled.'
        : 'There are no flags to review before kickoff.',
      { state: match.state },
    );
  }

  // Three questions about the same row, and the messages differ because the remedies do: a vanished
  // flag means reload, a foreign one means this sheet is showing another match, and a `voiceNote`
  // means the caller reached past DIST-1's queue for a row that was never in it.
  const flag = snap.flag;
  if (flag === undefined || flag.matchId !== cmd.matchId || flag.kind !== 'flag') {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_FLAG_NOT_FOUND',
      'That flag is no longer on this match.',
      { observationId: cmd.observationId, kind: flag?.kind ?? null },
    );
  }

  if (snap.alreadyResolved) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_FLAG_ALREADY_RESOLVED',
      'That flag has already been dealt with.',
      { observationId: cmd.observationId },
    );
  }

  const planned = planFor(cmd, snap);
  if (!planned.ok) return planned;

  const { row, hypothesisId, created } = planned.value;
  const resolution: FlagResolutionRow = {
    id: cmd.resolutionId,
    observationId: cmd.observationId,
    action: cmd.plan.kind === 'dismiss' ? 'dismissed' : 'promoted',
    resolvedAt: snap.now,
    promotedHypothesisId: hypothesisId,
  };

  return ok({
    // The hypothesis first: the resolution references it, and a reader inside this transaction
    // should never see a `promotedHypothesisId` pointing at a row the transaction has not written.
    ops:
      row === null
        ? [{ t: 'insertFlagResolution', row: resolution }]
        : [
            { t: 'insertHypothesis', row },
            { t: 'insertFlagResolution', row: resolution },
          ],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg: `FLAG-3: flag ${resolution.action}`,
        data: {
          observationId: cmd.observationId,
          matchMinute: flag.matchMinute,
          promotedHypothesisId: hypothesisId,
          createdHypothesis: created,
        },
      },
    ],
    // No postcondition. `boardCap` guards positions and this command cannot write one (INV-4);
    // `talkPointSet` is INV-6's `fullTime` rule and has nothing to do with a flag.
    post: [],
  });
}

interface PlannedPromotion {
  /** The hypothesis to insert, or `null` when the plan creates none (dismiss, attach). */
  readonly row: HypothesisRow | null;
  /** DIST-6's provenance on the resolution row. `null` for a dismissal. */
  readonly hypothesisId: string | null;
  readonly created: boolean;
}

/**
 * §10.1's three promote paths, validated — the part of this handler that differs per arm.
 *
 * Split out so the guards above read as one list of questions about the flag, and this reads as one
 * list of questions about what the user chose.
 *
 * The switch has **no trailing return**, which is what closes the union: a fourth arm added to
 * {@link FlagResolutionPlan} makes this function fail its declared return type rather than fall
 * through to whatever the last branch happened to be. That matters here more than usual — falling
 * through would file a *dismissal*, silently discarding a flag the user asked to promote.
 */
function planFor(
  cmd: ResolveFlagCommand,
  snap: ResolveFlagSnapshot,
): DomainResult<PlannedPromotion> {
  switch (cmd.plan.kind) {
    case 'dismiss':
      return ok({ row: null, hypothesisId: null, created: false });

    case 'attach': {
      // §3.2 — a `promotedHypothesisId` that points at nothing is a blank line on the share card
      // with nothing anywhere reporting why. Checked against rows read inside the transaction, so a
      // pattern deleted in the HT-2 sheet a moment ago cannot be attached to.
      const { hypothesisId } = cmd.plan;
      if (!snap.existingHypothesisIds.includes(hypothesisId)) {
        return reject(
          MODULE,
          cmd.matchId,
          'ERR_HYPOTHESIS_NOT_FOUND',
          'That pattern is no longer on this match.',
          { hypothesisId },
        );
      }
      return ok({ row: null, hypothesisId, created: false });
    }

    case 'create': {
      const { row } = cmd.plan;
      if (row.matchId !== cmd.matchId) {
        return reject(
          MODULE,
          cmd.matchId,
          'ERR_COMMAND_ROW_MISMATCH',
          'That pattern does not belong to this match.',
          { id: row.id },
        );
      }
      if (row.text.trim() === '') {
        return reject(
          MODULE,
          cmd.matchId,
          'ERR_HYPOTHESIS_TEXT_REQUIRED',
          'That pattern has no text.',
          { id: row.id },
        );
      }
      // The same list-only check PRE-3's add runs, and the same reason: the op type keeps the
      // *handler* off the board, this keeps the *caller* from smuggling a position in on the row.
      // §7.3 — a hypothesis created at halftime has count 0 and ranks last; it is not a shortcut to
      // a confirmed pattern.
      const problem = freshHypothesisProblem(row, cmd.matchId);
      if (problem !== null) {
        return reject(
          MODULE,
          cmd.matchId,
          'ERR_COMMAND_ROW_MISMATCH',
          'That pattern could not be created because it was malformed.',
          { id: row.id, problem },
        );
      }
      if (snap.existingHypothesisIds.includes(row.id)) {
        return reject(
          MODULE,
          cmd.matchId,
          'ERR_HYPOTHESIS_EXISTS',
          'That pattern is already on this match.',
          { id: row.id },
        );
      }
      return ok({ row, hypothesisId: row.id, created: true });
    }
  }
}
